import type { EditorFirstRunPath } from "../OnboardingFlow/onboardingFlowConfig.ts";

export type EditorOnboardingStepId =
  | "welcome"
  | "free-limit-warning"
  | "open-performer-roster"
  | "add-performers-modal"
  | "playback"
  | "add-page"
  | "select-mode"
  | "select-on-field"
  | "audio"
  | "export"
  | "done";

export const EDITOR_ONBOARDING_STORAGE_COMPLETED =
  "editorOnboarding_v2_completed";
export const EDITOR_ONBOARDING_STORAGE_DISMISSED =
  "editorOnboarding_v2_dismissed";

export interface OnboardingCompletionContext {
  isPlaying: boolean;
  pageCount: number;
  editMode: string;
  selectedPerformerCount: number;
  /** Total performers in the drill (roster). */
  performerCount: number;
  audioFile: string | undefined;
  showAudioModal: boolean;
  showExportDialog: boolean;
  showPerformerLabelModal: boolean;
}

export interface EditorOnboardingStepDef {
  id: EditorOnboardingStepId;
  title: string;
  body: string;
  /** data-tour attribute to spotlight; undefined = center card */
  targetTour?: string;
}

const STEP_WELCOME_DEFAULT: EditorOnboardingStepDef = {
  id: "welcome",
  title: "Welcome to DrillFlo",
  body: "We will start by getting performers on the field—that is the first step in any drill. After that, the tour covers playback, sets, moving performers, music, and printing. Click Next to begin, or Skip anytime.",
};

const STEP_WELCOME_BEGINNER: EditorOnboardingStepDef = {
  id: "welcome",
  title: "Welcome — let's build your first roster",
  body: "New to drill software? We will go step by step: add performers, place them on the field, then add a set and play it back. Skip anytime if you want to explore on your own.",
};

const STEP_FREE_LIMIT: EditorOnboardingStepDef = {
  id: "free-limit-warning",
  title: "Starter includes up to 50 performers",
  body: "You told us your ensemble is 100+. On Starter you can design with up to 50 performers. Start with a section or smaller roster to learn the tools, or upgrade to DrillFlo Pro anytime for unlimited performers.",
};

const STEP_OPEN_ROSTER: EditorOnboardingStepDef = {
  id: "open-performer-roster",
  title: "Open the performer roster",
  body: "Click Performers in the menu bar, then choose Performers again to open the roster where you add people to your drill.",
  targetTour: "performers-menu",
};

const STEP_ADD_PERFORMERS: EditorOnboardingStepDef = {
  id: "add-performers-modal",
  title: "Add performers to your drill",
  body: "Choose how many performers you need and a label letter, then click Add Performers. You need at least one performer on the roster before you can place them on the field.",
  targetTour: "performer-modal-add",
};

const STEP_ADD_PERFORMERS_BEGINNER: EditorOnboardingStepDef = {
  id: "add-performers-modal",
  title: "Add a few performers",
  body: "Start small—try 8–16 performers with a letter label (like A). Click Add Performers. You can always add more later.",
  targetTour: "performer-modal-add",
};

const STEP_PLAYBACK: EditorOnboardingStepDef = {
  id: "playback",
  title: "Play your drill",
  body: "Click Play to watch your drill run in time with counts. You can also press the spacebar while the editor is selected.",
  targetTour: "playback",
};

const STEP_ADD_PAGE: EditorOnboardingStepDef = {
  id: "add-page",
  title: "Add a new set",
  body: "Each page is a set in your show. Click the + on the timeline after Page 0 to add your next set.",
  targetTour: "add-page",
};

const STEP_SELECT_MODE: EditorOnboardingStepDef = {
  id: "select-mode",
  title: "Move performers",
  body: "Select the arrow tool on the left, then click and drag performers to adjust their position on the field.",
  targetTour: "select-mode",
};

const STEP_SELECT_MODE_BEGINNER: EditorOnboardingStepDef = {
  id: "select-mode",
  title: "Place performers on the field",
  body: "Choose the arrow tool on the left. Next you will select performers and drag them into a simple form.",
  targetTour: "select-mode",
};

const STEP_SELECT_ON_FIELD: EditorOnboardingStepDef = {
  id: "select-on-field",
  title: "Select performers on the field",
  body: "Click a performer on the field, or click and drag to select a group and move them together.",
  targetTour: "field-canvas",
};

const STEP_AUDIO: EditorOnboardingStepDef = {
  id: "audio",
  title: "Add your music",
  body: "Go to Project → Audio to upload your show music and sync it to your drill.",
  targetTour: "project-menu",
};

const STEP_EXPORT: EditorOnboardingStepDef = {
  id: "export",
  title: "Print or export your drill",
  body: "Go to Project → Print to export your drill to PDF.",
  targetTour: "project-menu",
};

const STEP_DONE: EditorOnboardingStepDef = {
  id: "done",
  title: "You are ready to design!",
  body: "If you need help, you can always check out the help center from the toolbar.",
};

const STEP_DONE_BEGINNER: EditorOnboardingStepDef = {
  id: "done",
  title: "Nice work — you have the basics",
  body: "You can keep building this show, or open Help anytime from the toolbar if you get stuck.",
};

/** Default tour order (existing product path). */
export const EDITOR_ONBOARDING_STEPS: EditorOnboardingStepDef[] = [
  STEP_WELCOME_DEFAULT,
  STEP_OPEN_ROSTER,
  STEP_ADD_PERFORMERS,
  STEP_PLAYBACK,
  STEP_ADD_PAGE,
  STEP_SELECT_MODE,
  STEP_SELECT_ON_FIELD,
  STEP_AUDIO,
  STEP_EXPORT,
  STEP_DONE,
];

/**
 * Build the first-run editor tour from onboarding answers.
 * - Beginners: roster → place on field → sets → playback (learn by doing).
 * - Large ensembles: early Starter 50-performer limit callout.
 */
export function getEditorOnboardingSteps(
  path: EditorFirstRunPath = { isBeginner: false, isLargeEnsemble: false }
): EditorOnboardingStepDef[] {
  const steps: EditorOnboardingStepDef[] = [];

  steps.push(path.isBeginner ? STEP_WELCOME_BEGINNER : STEP_WELCOME_DEFAULT);

  if (path.isLargeEnsemble) {
    steps.push(STEP_FREE_LIMIT);
  }

  steps.push(STEP_OPEN_ROSTER);
  steps.push(
    path.isBeginner ? STEP_ADD_PERFORMERS_BEGINNER : STEP_ADD_PERFORMERS
  );

  if (path.isBeginner) {
    // Roster-first: place performers before playback / sets
    steps.push(STEP_SELECT_MODE_BEGINNER);
    steps.push(STEP_SELECT_ON_FIELD);
    steps.push(STEP_ADD_PAGE);
    steps.push(STEP_PLAYBACK);
  } else {
    steps.push(STEP_PLAYBACK);
    steps.push(STEP_ADD_PAGE);
    steps.push(STEP_SELECT_MODE);
    steps.push(STEP_SELECT_ON_FIELD);
  }

  steps.push(STEP_AUDIO);
  steps.push(STEP_EXPORT);
  steps.push(path.isBeginner ? STEP_DONE_BEGINNER : STEP_DONE);

  return steps;
}

export function isStepSatisfiedByContext(
  stepId: EditorOnboardingStepId,
  ctx: OnboardingCompletionContext
): boolean {
  switch (stepId) {
    case "welcome":
      return true;
    case "free-limit-warning":
      return false;
    case "open-performer-roster":
      return ctx.showPerformerLabelModal;
    case "add-performers-modal":
      return ctx.performerCount > 0;
    case "playback":
      return ctx.isPlaying;
    case "add-page":
      return ctx.pageCount > 1;
    case "select-mode":
      return ctx.editMode === "SELECT_AND_MOVE";
    case "select-on-field":
      return ctx.selectedPerformerCount > 0;
    case "audio":
      return Boolean(
        (ctx.audioFile && ctx.audioFile.length > 0) || ctx.showAudioModal
      );
    case "export":
      return ctx.showExportDialog;
    case "done":
      return true;
    default:
      return false;
  }
}
